import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { TablePagination, Checkbox, useTheme } from "@material-ui/core";

import MaskedText from "../../../controls/MaskedText";
import CurrencyLookup from "./CurrencyLookup";

import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import ExchangeRateProvidersValidator from "../validators/ExchangeRateProvidersValidator";

import { useExchangeRateProviders, useAddExchangeRateProvider, useUpdateExchangeRateProvider, useDeleteExchangeRateProvider } from "../../../services/currencies";
import { defaultComponents } from '../../../controls/Material-Table-Components';

const ExchangeRateProviderList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(ExchangeRateProvidersValidator, false);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddExchangeRateProvider = useAddExchangeRateProvider();
  const handleUpdateExchangeRateProvider = useUpdateExchangeRateProvider();
  const handleDeleteExchangeRateProvider = useDeleteExchangeRateProvider();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const exchangeRateProviders = useExchangeRateProviders({tableRef, queryClient, extraKeys});

  const getCurrencyColumns = (props) => { 
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9]-",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(200),
        cellStyle: { padding: "8px 2px 8px 12px" },
        headerStyle: { padding: "8px 2px 8px 12px" },
        title: "Rate Provider Code",
        field: "rateProviderCode",
        editable: props.canEdit ? "onAdd" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(30)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Rate Provider Name",
        field: "rateProviderName",
        editable: props.canEdit ? "onAdd" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...(ColumnWidth(120)),
        title: "Base Currency",
        field: "baseCurrencyCode",
        editable: state.canEdit ? "onAdd" : "never",
        emptyValue: "",
        initialEditValue: "USD",
        editComponent: (props) => (
          <CurrencyLookup
            showChildCurrencies={false}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right"}}
          />
        ),
      },  
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "For System",
        field: "forSystem",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false, 
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.forSystem === true} value="true" />        
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "For Fuel",
        field: "forFuel",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false, 
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.forFuel === true} value="true" />        
      },
    ];
  };
  
  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1.,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Exchange Rate Provider Management"
            columns={getCurrencyColumns({canEdit: state.canEdit})}
            data={exchangeRateProviders.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddExchangeRateProvider.mutate({record: newData, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateExchangeRateProvider.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteExchangeRateProvider.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default ExchangeRateProviderList;
